import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import API from '../../../../services/API'
import { useParams } from 'react-router-dom'
import { Alert, Box, LinearProgress } from '@mui/material'
import ViewContents from './ViewContents'
import Home from './Home'
import TopBar from './TopBar'
import AssignToMe from './AssignToMe'
import AssignToVehicle from './AssignToVehicle'
import ReturnToStores from './ReturnToStores'
import Audit from '../Audit'
import getMedicineAlarm from '../getMedicineAlarm'
import { useDocumentTitle } from '../../../../helpers/DocumentTitleManager'
import DrawUpDialog from '../prepared/DrawUpDialog'
import { MyPreparedMedicinesPanel } from '../prepared/MyPreparedMedicines'
import { GlobalContext } from '../../../../App'
import { hasTierAccess } from '../../../../components/Subscriptions/SubscriptionGate'

const DrugBagPage = () => {
  const [bag, setBag] = useState({})
  const [requiredDrugs, setRequiredDrugs] = useState([])
  const [contents, setContents] = useState([])
  const [contentSummary, setContentSummary] = useState([])
  const [bagLevels, setBagLevels] = useState([])
  const [view, setView] = useState('home')
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  // Bumped after a draw-up so the "carrying" panel reloads
  const [preparedKey, setPreparedKey] = useState(0)
  const { id: drugBagID } = useParams()
  const {setPageTitle} = useDocumentTitle()
  // Drawn-up medicines are part of the Enterprise Critical Care suite
  const { tenantInfo } = useContext(GlobalContext)
  const canDrawUp = hasTierAccess(tenantInfo, 3)

  useEffect(() => {
    API.get(`/medicines-management/location?id=${drugBagID}`)
      .then((res) => {
        setContents(res.data.contents)
        setRequiredDrugs(res.data.requiredDrugs)
        setBag(res.data.location)
        setBagLevels(res.data.bagLevels ?? [])
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    contentsSummaryFn()
  }, [contents, bag, bagLevels])

  useEffect(() => {
    if (bag?.name) {
      setPageTitle(bag.name)
    }
  }, [bag?.name, setPageTitle])

  const contentsSummaryFn = () => {
    try {
      let Ar = []
      for (const drug of contents) {
        const i = Ar.findIndex((e) => e._id === drug.medicine._id)
        if (i > -1) {
          Ar[i].qty++
          Ar[i].expiry =
            moment(Ar[i].expiry) < moment(drug.expiry)
              ? Ar[i].expiry
              : drug.expiry
        } else {
          Ar.push({
            name: drug.medicine.title,
            _id: drug.medicine._id,
            constitution: drug.medicine?.constitution,
            presentation: drug.medicine?.presentation,
            expiry: drug.expiry,
            qty: 1,
            alarm: getMedicineAlarm(drug.medicine, bag, bagLevels),
          })
        }
      }
      const sortedAr = Ar.sort((a, b) =>
        a.name > b.name ? 1 : a.name < b.name ? -1 : 0
      )
      setContentSummary(sortedAr)
    } catch {
      setContentSummary([])
    }
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      position="relative"
      sx={{ pb: 3, maxWidth: 1180, mx: 'auto', width: '100%' }}
    >
      {loading ? (
        <LinearProgress />
      ) : fetchError ? (
        <Alert severity="error">
          Unable to load this medicine bag. Please try again later.
        </Alert>
      ) : (
        <>
          <TopBar bag={bag} view={view} setView={setView} />
          {(view === 'home' || view === 'drawUp') && (
            <Home setView={setView} bagID={bag._id} />
          )}
          {canDrawUp && (view === 'home' || view === 'drawUp') && (
            <MyPreparedMedicinesPanel
              title="Drawn-up medicines you are carrying"
              hideWhenEmpty
              refreshKey={preparedKey}
            />
          )}
          {canDrawUp && (
            <DrawUpDialog
              open={view === 'drawUp'}
              onClose={() => setView('home')}
              bagID={bag._id}
              bagLabel={`${bag.number ? `${bag.number} - ` : ''}${bag.name ?? ''}`}
              onDrawn={() => setPreparedKey((key) => key + 1)}
            />
          )}

          {view === 'contents' && (
            <ViewContents
              drugBag={bag}
              setView={setView}
              requiredDrugs={requiredDrugs}
            />
          )}
          {view === 'assignToMe' && (
            <AssignToMe drugBag={bag} setView={setView} />
          )}
          {view === 'assignToVehicle' && (
            <AssignToVehicle drugBag={bag} setView={setView} />
          )}
          {view === 'returnToStores' && (
            <ReturnToStores drugBag={bag} setView={setView} />
          )}
          {view === 'conductAudit' && (
            <Audit
              open={view === 'conductAudit'}
              onClose={() => setView('home')}
              location={bag}
              contentSummary={contentSummary}
              contents={contents}
            />
          )}
        </>
      )}
    </Box>
  )
}

export default DrugBagPage
